import React from 'react';
import { Box, Button, Drawer, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import * as TranslationsActions from 'actions/TranslationsActions';
import * as TranslationsSelectors from 'selectors/TranslationsSelectors';
import ContainerBox from 'components/ContainerBox';
import TranslatableGroup from '../components/TranslatableGroup';
import TranslatableGroupPreview from '../components/TranslatableGroupPreview';
import { Languages, Translatable } from 'common/modules/translations';
import EmptyState from 'common/components/states/EmptyState';
import { Routes, push } from 'routing';
import { ById } from 'common/types';
import { getQueryParam } from 'common/utils/frontUtils';
import SetPageTitle from 'components/Portals/SetPageTitle';
import { useTranslation } from 'services/localization';
import { getLanguageName } from 'common/locales/utils';
import TranslationErrorBanner from '../components/TranslationErrorBanner';
import { useTranslationState } from '../services/useTranslationState';
import { TranslationGroup } from 'reducers/TranslationsReducer';
import HasFeature from 'components/features/HasFeature';
import LanguagePromoPage from 'components/features/promos/LanguagePromoPage';

const Translations = () => {
	const { t } = useTranslation();
	const translationsState = useTranslationState();
	const dispatch = useDispatch();
	const location = useLocation();

	const activeLanguage = useSelector(TranslationsSelectors.activeLanguage);
	const translatableLanguages = useSelector(TranslationsSelectors.translatableLanguages);
	const someTranslatables = useSelector(TranslationsSelectors.someTranslatables);
	const activeGroup = getQueryParam('group');

	const handleLanguageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(TranslationsActions.setLanguage(e.target.value as Languages));
	};

	const translatableGroups: {
		id: TranslationGroup;
		title: string;
		translatablesById: ById<Translatable>;
	}[] = [
		{
			id: 'products',
			title: t('common:products.products', 'Products'),
			translatablesById: translationsState.products,
		},
		{
			id: 'categories',
			title: t('common:marketing.categories', 'Categories'),
			translatablesById: translationsState.categories,
		},
		{
			id: 'stores',
			title: t('common:shopLocation.stores', 'Stores'),
			translatablesById: translationsState.stores,
		},
		{
			id: 'pricingTables',
			title: t('pricing.pricingTemplates', 'Pricing templates'),
			translatablesById: translationsState.pricingTables,
		},
		{
			id: 'manualPaymentMethods',
			title: t('account.payments.manualPaymentMethods', 'Manual payment methods'),
			translatablesById: translationsState.manualPaymentMethods,
		},
		{
			id: 'miscellaneous',
			title: t('common:phrases.miscellaneous', 'Miscellaneous'),
			translatablesById: translationsState.miscellaneous,
		},
		{
			id: 'skidata',
			title: 'Skidata',
			translatablesById: translationsState.skidata,
		},
		{
			id: 'customizations',
			title: t('translations.subHeading.storeCustomizations', 'Store customizations'),
			translatablesById: translationsState.customizations,
		},
	];

	const renderGroups = (language: Languages) => {
		return translatableGroups.map((group) => (
			<Box key={group.id}>
				<TranslatableGroupPreview
					id={group.id}
					title={group.title}
					translatablesById={group.translatablesById}
				/>
				<Drawer
					anchor="right"
					open={activeGroup === group.id}
					onClose={() => push(Routes.LanguagesTab('translations'))}
					variant="temporary"
					PaperProps={{
						sx: {
							width: '100%',
							maxWidth: 1000,
							backgroundColor: (theme) => theme.palette.background.default,
						},
					}}
				>
					<TranslatableGroup
						title={group.title}
						translatablesById={group.translatablesById}
						group={group.id}
						onClose={() => push(Routes.LanguagesTab('translations'))}
					/>
				</Drawer>
			</Box>
		));
	};

	if (!translatableLanguages.length || !someTranslatables) {
		return (
			<HasFeature
				requiredFeature="LANGUAGES"
				fallback={(failedChecks) => {
					if (failedChecks.user) return <Redirect to={Routes.ACCOUNT_LANGUAGES} />;
					return <LanguagePromoPage />;
				}}
			>
				<EmptyState
					title={t('account.languages.translations.emptyStateTitle', 'Nothing to translate')}
					description={
						!someTranslatables
							? t(
									'account.languages.translations.emptyStateDescriptionNothingToTranslate',
									'To enter translations here, make sure you have something to translate, e.g. a product name or description',
							  )
							: t(
									'account.languages.translations.emptyStateDescription',
									'To enter translations here, make sure you have more than one language enabled',
							  )
					}
					cta={
						someTranslatables ? (
							<Button
								color="primary"
								variant="contained"
								onClick={() => push(Routes.LanguagesTab('settings'))}
							>
								{t('account.languages.translations.emptyStateCta', 'Enable more languages')}
							</Button>
						) : (
							<Button
								color="primary"
								variant="contained"
								onClick={() =>
									push(Routes.CATALOG_CREATE_PRODUCT, {
										from: Routes.LanguagesTab('translations'),
									})
								}
							>
								{t(
									'account.languages.translations.emptyStateCtaNothingToTranslate',
									'Add a product',
								)}
							</Button>
						)
					}
				/>
			</HasFeature>
		);
	}

	return (
		<>
			<SetPageTitle priority={3} title={t('account.languages.pageTitle', 'Languages')} />
			<HasFeature
				requiredFeature="LANGUAGES"
				fallback={(failedChecks) => {
					if (failedChecks.user) return <Redirect to={Routes.ACCOUNT_LANGUAGES} />;
					return <LanguagePromoPage />;
				}}
			>
				<TranslationErrorBanner hidden={location.search !== ''} />

				<ContainerBox>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<Typography variant="h6">
								{t('account.languages.translations.selectLanguage', 'Select language')}
							</Typography>
							<Typography variant="body2" color="textSecondary">
								{t(
									'account.languages.translations.languagesToEdit',
									'Which language do you want to edit translations for?',
								)}
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField select fullWidth value={activeLanguage} onChange={handleLanguageChange}>
								{translatableLanguages.map((language) => (
									<MenuItem value={language} key={language}>
										{getLanguageName(language, t)}
									</MenuItem>
								))}
							</TextField>
						</Grid>
					</Grid>
				</ContainerBox>
				<ContainerBox padding="none">{renderGroups(activeLanguage)}</ContainerBox>
			</HasFeature>
		</>
	);
};

export default Translations;
